/**
 * Comments API - Delete Operations
 *
 * Soft-delete functionality and notification cleanup.
 */

import { collection, query, where, getDocs, writeBatch } from "firebase/firestore";
import { auth } from "../../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";

/**
 * Soft-deletes all notifications that reference a given commentId.
 * Covers: type 'comment', 'reply', 'mention', and 'like' (targetType: 'comment').
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} commentId
 * @param {import('firebase/firestore').Timestamp} serverNow
 * @param {import('firebase/firestore').WriteBatch} batch  (optional — if provided, adds to existing batch)
 * @returns {Promise<void | import('firebase/firestore').WriteBatch>}
 */
export async function softDeleteCommentNotifications(db, commentId, serverNow, batch) {
  try {
    const notifQuery = query(
      collection(db, "notifications"),
      where("commentId", "==", commentId),
    );
    const notifSnap = await getDocs(notifQuery);

    if (notifSnap.empty) return batch;

    // Use provided batch or create a new one (for atomic grouping)
    const useBatch = batch || writeBatch(db);

    notifSnap.docs.forEach((docSnap) => {
      useBatch.update(docSnap.ref, { deleted: true });
    });

    if (!batch) {
      await useBatch.commit();
    }
  } catch (error) {
    // Notification cleanup is best-effort — don't fail the comment deletion
    console.warn(
      "[deleteComment] Failed to clean up notifications for comment:",
      commentId,
      error,
    );
  }
}

/**
 * Soft-deletes a comment AND all its replies atomically.
 *
 * PHASE 1 FIX: Now uses Cloud Function with Firestore Transaction for true atomicity.
 * This eliminates the race condition where replies could be added during chunked deletion.
 *
 * @param {string} commentId
 */
export const deleteComment = async (commentId) => {
  try {
    const currentUid = auth?.currentUser?.uid || null;
    if (!currentUid) {
      throw new Error("You must be logged in to delete a comment");
    }

    // Call atomic Cloud Function for true transactional delete
    const deleteCommentAtomic = httpsCallable(functions, 'deleteCommentAtomic');
    const result = await deleteCommentAtomic({
      commentId,
      requesterId: currentUid
    });

    if (result.data?.success || result.data?.alreadyDeleted) {
      return true;
    } else {
      throw new Error(result.data?.message || "Failed to delete comment");
    }
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};
